import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ChatCircleDots, Newspaper, PaperPlaneRight, LockSimple, Snowflake, Rocket,
  Bandaids, Prohibit, Flag, ArrowBendUpLeft, X, Users, Circle, ArrowCounterClockwise,
  ArrowsClockwise, Scales, ArrowSquareOut,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { patchPlayerOverride } from "@/lib/overridesStore";

const REACTIONS = [
  { kind: "lock", label: "Lock", Icon: LockSimple, color: "#39FF14" },
  { kind: "fade", label: "Fade", Icon: Snowflake, color: "#4DA6FF" },
  { kind: "smash", label: "Smash", Icon: Rocket, color: "#FFCC00" },
  { kind: "hurt", label: "Hurt", Icon: Bandaids, color: "#FF3B30" },
];

const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

function NewsCard({ item, onReact, onDiscuss }) {
  const [restoring, setRestoring] = useState(false);
  const [projText, setProjText] = useState("");
  const [rebuilding, setRebuilding] = useState(false);
  const total = Object.values(item.reactions || {}).reduce((a, b) => a + b, 0);
  const sev = item.severity === "high" ? "#FF3B30" : item.severity === "mid" ? "#FF8A00" : "#3f3f46";

  const restore = () => {
    const v = Number(projText);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error("Enter the projection you want to use", {
        description: "A restored player needs your own number — we won't guess one.",
      });
      return;
    }
    patchPlayerOverride(item.dk_id, { exclude: false, proj_override: v, restore: true });
    setRestoring(false);
    setProjText("");
    toast.success(`${item.player_name} restored at ${v.toFixed(1)}`, {
      description: "Rebuild to push him into lineups.",
    });
  };

  const rebuild = async (source) => {
    setRebuilding(true);
    try {
      const r = await api.hubRebuildAffected(item.dk_id, source);
      const n = (r.fixed || []).length;
      const failed = (r.failed || []).length;
      toast.success(n ? `Repaired ${n} lineup${n === 1 ? "" : "s"}` : "No lineups held him", {
        description: failed ? `${failed} couldn't be swapped — open them manually.` : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div className="border border-zinc-800 bg-[#0e0e0e] p-3" data-testid={`news-card-${item.id}`}>
      <div className="flex items-start gap-2">
        <span className="mt-1 w-1.5 h-1.5 flex-shrink-0" style={{ background: sev }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-700 text-zinc-400"
                  data-testid={`news-source-${item.id}`}>
              {item.source || item.kind}
            </span>
            {(item.tags || []).map((t) => (
              <span key={t} className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5"
                    style={{ color: sev, background: `${sev}1a`, border: `1px solid ${sev}66` }}
                    data-testid={`news-tag-${item.id}-${t.replace(/\s+/g, "-").toLowerCase()}`}>
                {t}
              </span>
            ))}
            {item.source_url && (
              <a href={item.source_url} target="_blank" rel="noreferrer"
                 data-testid={`news-link-${item.id}`}
                 className="mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] inline-flex items-center gap-1">
                <ArrowSquareOut size={10} /> Read
              </a>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[13px] font-bold text-zinc-100 leading-snug">{item.title}</span>
            <span className="mono text-[9px] text-zinc-600 flex-shrink-0">{ago(item.created_at)} ago</span>
          </div>
          <div className="mono text-[10px] text-zinc-500 mt-0.5">{item.detail}</div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {REACTIONS.map(({ kind, label, Icon, color }) => {
              const n = (item.reactions || {})[kind] || 0;
              const mine = item.my_reaction === kind;
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={() => onReact(item, kind)}
                  data-testid={`news-react-${kind}-${item.id}`}
                  title={`${label}${n ? ` · ${n}` : ""}`}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest transition-colors ${
                    mine ? "font-bold" : "text-zinc-500 border-zinc-800 hover:border-zinc-600"
                  }`}
                  style={mine ? { color, borderColor: color, background: `${color}1a` } : undefined}
                >
                  <Icon size={10} weight="fill" /> {label}{n ? ` ${n}` : ""}
                </button>
              );
            })}
            {item.dk_id && (
              <button
                type="button"
                onClick={() => {
                  patchPlayerOverride(item.dk_id, { exclude: true });
                  toast.success(`${item.player_name} removed from your pool`, {
                    description: "Rebuild to push the change into lineups.",
                  });
                }}
                data-testid={`news-mark-out-${item.id}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-zinc-800 text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30] mono text-[9px] uppercase tracking-widest transition-colors"
              >
                <Prohibit size={10} /> Mark out
              </button>
            )}
            {item.dk_id && (
              <>
                <button
                  type="button"
                  onClick={() => setRestoring((v) => !v)}
                  data-testid={`news-restore-${item.id}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14] mono text-[9px] uppercase tracking-widest transition-colors"
                >
                  <ArrowCounterClockwise size={10} /> Restore
                </button>
                <button
                  type="button"
                  onClick={() => rebuild("smart")}
                  disabled={rebuilding}
                  data-testid={`news-rebuild-${item.id}`}
                  title="Surgically re-solve only the lineups holding this player — everything else stays as you built it."
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-zinc-800 text-zinc-400 hover:border-[#FFCC00] hover:text-[#FFCC00] mono text-[9px] uppercase tracking-widest transition-colors disabled:opacity-40"
                >
                  <ArrowsClockwise size={10} /> {rebuilding ? "Fixing…" : "Rebuild affected"}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => onDiscuss(item)}
              data-testid={`news-discuss-${item.id}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-zinc-800 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14] mono text-[9px] uppercase tracking-widest transition-colors"
            >
              <ChatCircleDots size={10} /> Discuss
            </button>
            {total > 0 && (
              <span className="mono text-[9px] text-zinc-600 ml-auto">{total} reaction{total === 1 ? "" : "s"}</span>
            )}
          </div>
          {restoring && (
            <div className="flex items-center gap-2 mt-2 border border-[#39FF14]/40 bg-[#39FF14]/5 px-2 py-1.5"
                 data-testid={`news-restore-box-${item.id}`}>
              <span className="mono text-[9px] uppercase tracking-widest text-[#39FF14]">Your projection</span>
              <input
                autoFocus
                value={projText}
                onChange={(e) => setProjText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") restore(); }}
                data-testid={`news-restore-input-${item.id}`}
                placeholder="e.g. 14.5"
                className="w-20 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-1.5 py-0.5 mono text-[11px] text-zinc-100"
              />
              <button type="button" onClick={restore} data-testid={`news-restore-confirm-${item.id}`}
                className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black font-bold">
                Restore
              </button>
              <button type="button" onClick={() => setRestoring(false)} className="text-zinc-600 hover:text-white ml-auto">
                <X size={11} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SocialHub({ compact = false, onClose }) {
  const [view, setView] = useState("chat");
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(() => {
    try { return localStorage.getItem("wp_hub_room") || "lobby"; } catch { return "lobby"; }
  });
  const [messages, setMessages] = useState([]);
  const [news, setNews] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [muted, setMuted] = useState([]);
  const [rules, setRules] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const bottomRef = useRef(true);

  const activeRoom = useMemo(() => rooms.find((r) => r.id === room) || null, [rooms, room]);

  const loadRooms = useCallback(async () => {
    try {
      const r = await api.hubRooms();
      setRooms(r.rooms || []);
      if (!(r.rooms || []).some((x) => x.id === room)) setRoom("lobby");
    } catch { /* panel stays usable */ }
  }, [room]);

  const loadMessages = useCallback(async () => {
    try {
      const r = await api.hubMessages(room);
      setMessages(r.messages || []);
    } catch { /* ignore poll blips */ }
  }, [room]);

  const loadNews = useCallback(async () => {
    try {
      const r = await api.hubNews();
      setNews(r.news || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadRooms(); loadNews(); api.hubMutes().then((m) => setMuted(m.muted || [])).catch(() => {}); api.hubRules().then(setRules).catch(() => {}); }, []);
  useEffect(() => {
    try { localStorage.setItem("wp_hub_room", room); } catch { /* noop */ }
    loadMessages();
  }, [room, loadMessages]);
  useEffect(() => {
    // Poll, don't socket: builds peg this worker's CPU and would drop sockets.
    const id = setInterval(() => { loadMessages(); }, 3500);
    const nid = setInterval(() => { loadNews(); }, 60000);
    return () => { clearInterval(id); clearInterval(nid); };
  }, [loadMessages, loadNews]);
  useEffect(() => {
    if (bottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.hubPost({ room, text: t, reply_to: replyTo?.id || null });
      setText("");
      setReplyTo(null);
      bottomRef.current = true;
      await loadMessages();
      loadRooms();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const react = async (item, kind) => {
    try {
      const r = await api.hubReact(item.id, kind);
      setNews((prev) => prev.map((n) => (n.id === item.id
        ? { ...n, reactions: r.reactions, my_reaction: r.mine } : n)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not react");
    }
  };

  const discuss = (item) => {
    setView("chat");
    if (item.room && rooms.some((r) => r.id === item.room)) setRoom(item.room);
    setText(`re: ${item.title} — `);
  };

  const mute = async (uid, name) => {
    try {
      const r = await api.hubMute(uid);
      setMuted(r.muted || []);
      toast.success(`Muted ${name}`, { description: "You won't see their messages again." });
      loadMessages();
    } catch { toast.error("Could not mute"); }
  };

  const report = async (mid) => {
    try {
      const r = await api.hubReport(mid);
      toast.success("Reported", { description: r.auto_hidden ? "Hidden — enough reports." : "Thanks, we'll review it." });
    } catch { toast.error("Could not report"); }
  };

  const TABS = [
    { key: "chat", label: "Chat", Icon: ChatCircleDots },
    { key: "news", label: "News", Icon: Newspaper, badge: news.length },
  ];

  return (
    <div className={`flex flex-col ${compact ? "h-full" : "h-[calc(100vh-240px)] min-h-[520px]"}`} data-testid="social-hub">
      <div className="flex items-center border-b border-zinc-800 bg-[#0a0a0a]">
        {TABS.map(({ key, label, Icon, badge }) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            data-testid={`hub-tab-${key}`}
            className={`flex-1 px-3 py-2.5 mono text-[10px] uppercase tracking-widest inline-flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
              view === key ? "border-[#39FF14] text-[#39FF14]" : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Icon size={12} weight="fill" /> {label}
            {badge ? <span className="mono text-[9px] text-zinc-600">{badge}</span> : null}
          </button>
        ))}
        {onClose && (
          <button type="button" onClick={onClose} data-testid="hub-close"
            className="px-3 py-2.5 text-zinc-500 hover:text-white"><X size={14} weight="bold" /></button>
        )}
      </div>

      {view === "chat" && (
        <div className="flex flex-1 min-h-0">
          <div className={`${compact ? "w-[38%]" : "w-64"} border-r border-zinc-800 overflow-y-auto bg-black/30`} data-testid="hub-room-list">
            {rooms.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRoom(r.id)}
                data-testid={`hub-room-${r.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-zinc-900 transition-colors ${
                  room === r.id ? "bg-[#39FF14]/10 border-l-2 border-l-[#39FF14]" : "hover:bg-zinc-900/50"
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <Circle size={7} weight="fill" className={r.locked ? "text-zinc-700" : "text-[#39FF14]"} />
                  <span className="mono text-[10px] uppercase tracking-widest text-zinc-300 truncate">{r.label}</span>
                </div>
                <div className="mono text-[9px] text-zinc-600 mt-0.5 truncate">
                  {r.locked ? "locked · read-only" : r.sub}
                  {r.messages ? ` · ${r.messages} msg` : ""}
                </div>
              </button>
            ))}
          </div>

          <div className="flex-1 min-w-0 flex flex-col">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2"
              onScroll={(e) => {
                const el = e.currentTarget;
                bottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
              }}
              data-testid="hub-messages">
              {messages.length === 0 && (
                <div className="mono text-[10px] text-zinc-600 text-center py-10" data-testid="hub-empty">
                  Nothing here yet — say something and own the room.
                </div>
              )}
              {messages.map((m) => {
                const parent = m.reply_to ? messages.find((x) => x.id === m.reply_to) : null;
                return (
                  <div key={m.id} className="group" data-testid={`hub-msg-${m.id}`}>
                    {parent && (
                      <div className="mono text-[9px] text-zinc-600 truncate pl-3 border-l border-zinc-800">
                        <ArrowBendUpLeft size={9} className="inline mr-1" />
                        {parent.name}: {parent.text}
                      </div>
                    )}
                    <div className="flex items-baseline gap-2">
                      <span className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold flex-shrink-0">{m.name}</span>
                      <span className="mono text-[9px] text-zinc-700 flex-shrink-0">{ago(m.created_at)}</span>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 ml-auto flex-shrink-0">
                        <button type="button" onClick={() => setReplyTo(m)} title="Reply"
                          data-testid={`hub-reply-${m.id}`} className="text-zinc-600 hover:text-[#39FF14]">
                          <ArrowBendUpLeft size={11} />
                        </button>
                        <button type="button" onClick={() => report(m.id)} title="Report"
                          data-testid={`hub-report-${m.id}`} className="text-zinc-600 hover:text-[#FF8A00]">
                          <Flag size={11} />
                        </button>
                        <button type="button" onClick={() => mute(m.user_id, m.name)} title="Mute this user"
                          data-testid={`hub-mute-${m.id}`} className="text-zinc-600 hover:text-[#FF3B30]">
                          <Prohibit size={11} />
                        </button>
                      </div>
                    </div>
                    <div className="text-[13px] text-zinc-200 leading-snug break-words">{m.text}</div>
                  </div>
                );
              })}
            </div>

            {replyTo && (
              <div className="px-3 py-1.5 border-t border-zinc-800 flex items-center gap-2 bg-black/40">
                <span className="mono text-[9px] text-zinc-500 truncate">replying to {replyTo.name}: {replyTo.text}</span>
                <button type="button" onClick={() => setReplyTo(null)} className="ml-auto text-zinc-600 hover:text-white"><X size={11} /></button>
              </div>
            )}
            {rules && (
              <div className="px-3 py-1 border-t border-zinc-900 mono text-[9px] text-zinc-600 flex items-start gap-1.5"
                   data-testid="hub-house-rules">
                <Scales size={11} className="flex-shrink-0 mt-[1px]" />
                <span>{rules.rules}</span>
              </div>
            )}
            <div className="border-t border-zinc-800 p-2 flex items-center gap-2">
              <input
                value={text}                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                disabled={activeRoom?.locked}
                maxLength={600}
                data-testid="hub-input"
                placeholder={activeRoom?.locked ? "This slate has locked — read-only" : `Message ${activeRoom?.label || "lobby"}…`}
                className="flex-1 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2.5 py-2 text-[13px] text-zinc-100 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !text.trim() || activeRoom?.locked}
                data-testid="hub-send"
                className="px-3 py-2 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#39FF14] transition-colors"
              >
                <PaperPlaneRight size={14} weight="fill" />
              </button>
            </div>
            {muted.length > 0 && (
              <div className="px-3 py-1 mono text-[9px] text-zinc-600 border-t border-zinc-900" data-testid="hub-muted-count">
                {muted.length} user{muted.length === 1 ? "" : "s"} muted
              </div>
            )}
          </div>
        </div>
      )}

      {view !== "chat" && (
        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="hub-news-list">
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
            Beat-writer &amp; team RSS · Vegas line moves · DK slate changes · injury designations — newest first
          </div>
          {news.length === 0 && (
            <div className="mono text-[10px] text-zinc-600 text-center py-10">
              Nothing on the wire yet. New designations, line moves and beat reports land here within a minute.
            </div>
          )}
          {news.map((n) => (
            <NewsCard key={n.id} item={n} onReact={react} onDiscuss={discuss} />
          ))}
        </div>
      )}
    </div>
  );
}

export function HubRoomCount({ rooms }) {
  return (
    <span className="mono text-[9px] text-zinc-600 inline-flex items-center gap-1">
      <Users size={10} /> {rooms}
    </span>
  );
}
